import React from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { FaChartLine, FaCalendarAlt, FaDollarSign, FaUserNurse } from 'react-icons/fa';
import sampleData from '../../data/sampleData';
import './Analytics.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Analytics = () => {
  const { analyticsData } = sampleData;

  const revenueData = {
    labels: analyticsData.revenueByMonth.map(item => item.month),
    datasets: [
      {
        label: 'Revenue',
        data: analyticsData.revenueByMonth.map(item => item.revenue),
        fill: true,
        backgroundColor: 'rgba(80, 200, 172, 0.1)',
        borderColor: '#50C8AC',
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: '#50C8AC',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverRadius: 7
      }
    ]
  };

  const bookingsData = {
    labels: analyticsData.revenueByMonth.map(item => item.month),
    datasets: [
      {
        label: 'Bookings',
        data: analyticsData.revenueByMonth.map(item => item.bookings),
        backgroundColor: '#4834d4',
        borderRadius: 8
      }
    ]
  };

  const topServicesData = {
    labels: analyticsData.topServices.map(item => item.name),
    datasets: [
      {
        data: analyticsData.topServices.map(item => item.revenue),
        backgroundColor: [
          '#50C8AC',
          '#4834d4',
          '#26de81',
          '#fed330',
          '#fc5c65'
        ],
        borderWidth: 0
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#50C8AC',
        borderWidth: 1
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: {
            size: 12
          },
          usePointStyle: true
        }
      }
    }
  };

  return (
    <div className="analytics-page">
      <div className="page-header">
        <div>
          <h1>Analytics & Reports</h1>
          <p className="page-subtitle">Comprehensive platform performance metrics</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary">
            <FaCalendarAlt /> Custom Date Range
          </button>
          <button className="btn btn-primary">
            <FaChartLine /> Generate Report
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="analytics-stats">
        <div className="stat-card gradient-1">
          <div className="stat-icon">
            <FaDollarSign />
          </div>
          <div className="stat-content">
            <h3>$790,000</h3>
            <p>Total Revenue (YTD)</p>
            <span className="stat-change positive">+22.5%</span>
          </div>
        </div>
        <div className="stat-card gradient-2">
          <div className="stat-icon">
            <FaCalendarAlt />
          </div>
          <div className="stat-content">
            <h3>4,123</h3>
            <p>Total Bookings (YTD)</p>
            <span className="stat-change positive">+18.3%</span>
          </div>
        </div>
        <div className="stat-card gradient-3">
          <div className="stat-icon">
            <FaUserNurse />
          </div>
          <div className="stat-content">
            <h3>145</h3>
            <p>Active Providers</p>
            <span className="stat-change positive">+12%</span>
          </div>
        </div>
        <div className="stat-card gradient-4">
          <div className="stat-icon">
            <FaChartLine />
          </div>
          <div className="stat-content">
            <h3>$191.75</h3>
            <p>Avg. Booking Value</p>
            <span className="stat-change positive">+5.2%</span>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="analytics-charts-grid">
        <div className="chart-card large">
          <div className="chart-header">
            <h3>Revenue Trend</h3>
            <p className="chart-subtitle">Monthly revenue for the year</p>
          </div>
          <div className="chart-container">
            <Line data={revenueData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Monthly Bookings</h3>
            <p className="chart-subtitle">Booking volume by month</p>
          </div>
          <div className="chart-container">
            <Bar data={bookingsData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Top Services by Revenue</h3>
            <p className="chart-subtitle">Revenue distribution</p>
          </div>
          <div className="chart-container-doughnut">
            <Doughnut data={topServicesData} options={doughnutOptions} />
          </div>
        </div>
      </div>

      {/* Top Services Table */}
      <div className="content-card">
        <div className="card-header">
          <h3>Top Performing Services</h3>
        </div>
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Service Name</th>
                <th>Total Bookings</th>
                <th>Revenue Generated</th>
                <th>Avg. Rating</th>
                <th>Growth</th>
              </tr>
            </thead>
            <tbody>
              {analyticsData.topServices.map((service, index) => (
                <tr key={index}>
                  <td>
                    <span className="service-name">{service.name}</span>
                  </td>
                  <td>
                    <span className="booking-count">{service.bookings}</span>
                  </td>
                  <td>
                    <span className="amount">${service.revenue.toLocaleString()}</span>
                  </td>
                  <td>
                    <span className="rating-badge">⭐ 4.{8 - index}</span>
                  </td>
                  <td>
                    <span className="stat-change positive">+{15 + index * 2}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analytics;